// ─── Enums ───────────────────────────────────────────────────────────────────

export enum ReminderStatus {
  Pending = "pending",
  Sent = "sent",
  Acknowledged = "acknowledged",
  Snoozed = "snoozed",
  Missed = "missed",
}

export enum PetMood {
  Ecstatic = "ecstatic",
  Happy = "happy",
  Content = "content",
  Neutral = "neutral",
  Sad = "sad",
  Unhappy = "unhappy",
  Distressed = "distressed",
}

export enum ReminderAction {
  Done = "done",
  Snooze = "snooze",
  Skip = "skip",
}

export enum DayOfWeek {
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Sunday = 7,
}

// ─── Core Domain Types ────────────────────────────────────────────────────────

export interface Family {
  id: string;
  name: string;
  telegramGroupId?: string;
  createdAt: Date;
  updatedAt: Date;
  parents: Parent[];
  children: Child[];
  pet: PetState;
}

export interface Parent {
  id: string;
  familyId: string;
  telegramUserId: string;
  firstName: string;
  lastName?: string;
  languageCode: string;
  isAdmin: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Child {
  id: string;
  familyId: string;
  name: string;
  birthDate?: Date;
  avatarEmoji: string;
  createdAt: Date;
  updatedAt: Date;
  routines: Routine[];
}

export interface Routine {
  id: string;
  childId: string;
  familyId: string;
  name: string;
  description?: string;
  isActive: boolean;
  steps: RoutineStep[];
  schedules: StepSchedule[];
  createdAt: Date;
  updatedAt: Date;
}

export interface RoutineStep {
  id: string;
  routineId: string;
  order: number;
  title: string;
  description?: string;
  durationMinutes: number;
  emoji: string;
  ttsText?: string;
  imageUrl?: string;
}

export interface StepSchedule {
  id: string;
  routineId: string;
  stepId: string;
  dayOfWeek: DayOfWeek;
  hour: number;
  minute: number;
  timezone: string;
  isActive: boolean;
}

export interface ReminderInstance {
  id: string;
  familyId: string;
  childId: string;
  routineId: string;
  stepId: string;
  scheduleId: string;
  scheduledAt: Date;
  sentAt?: Date;
  acknowledgedAt?: Date;
  status: ReminderStatus;
  telegramMessageId?: number;
  snoozedUntil?: Date;
  snoozeCount: number;
}

// ─── Pet / Gamification ───────────────────────────────────────────────────────

export interface PetState {
  id: string;
  familyId: string;
  name: string;
  happiness: number;       // 0–100
  streakDays: number;
  lastInteractionAt: Date;
  mood: PetMood;
  level: number;
  experiencePoints: number;
  createdAt: Date;
  updatedAt: Date;
}

// ─── API Request / Response shapes ───────────────────────────────────────────

export interface HealthResponse {
  status: "ok";
  timestamp: string;
  version: string;
}

export interface ApiError {
  statusCode: number;
  error: string;
  message: string;
}

export interface CreateFamilyRequest {
  name: string;
  telegramGroupId?: string;
}

export interface CreateChildRequest {
  familyId: string;
  name: string;
  birthDate?: string;
  avatarEmoji?: string;
}

export interface CreateRoutineRequest {
  childId: string;
  familyId: string;
  name: string;
  description?: string;
  steps: Omit<RoutineStep, "id" | "routineId">[];
}

export interface AcknowledgeReminderRequest {
  reminderId: string;
  action: ReminderAction;
  snoozeMinutes?: number;
}

// ─── Telegram Bot Context ─────────────────────────────────────────────────────

export interface TelegramUserContext {
  telegramUserId: string;
  firstName: string;
  lastName?: string;
  languageCode: string;
  familyId?: string;
  isAdmin: boolean;
}

// ─── Worker Job Payloads ──────────────────────────────────────────────────────

export interface SendReminderJobPayload {
  reminderId: string;
  familyId: string;
  childId: string;
  stepId: string;
  telegramGroupId: string;
}

export interface CheckMissedRemindersJobPayload {
  familyId: string;
  windowMinutes: number;
}

export interface UpdatePetStateJobPayload {
  familyId: string;
  petId: string;
}
